import React, { useState } from "react";
import {
  PIECE_DEFINITIONS,
  getPiecesByClass,
} from "../data/pieceDefinitions.ts";
import { getPieceValues } from "../data/pieceValues.ts";
import PieceValueGraph from "./PieceValueGraph.tsx";
import PieceMovementDiagram from "./PieceMovementDiagram.tsx";
import { PieceClass } from "../types/Board";
import PageHeader from "./PageHeader.tsx";
import { TAB_DESCRIPTIONS } from "../constants/tabDescriptions.ts";

const PieceCatalog: React.FC = () => {
  const [selectedClasses, setSelectedClasses] = useState<Array<PieceClass | "all">>(["all"]);
  const [selectedLevels, setSelectedLevels] = useState<Array<PieceLevel | "all">>(["all"]);
  const [selectedPieceForGraph, setSelectedPieceForGraph] = useState<
    string | null
  >(null);
  const [selectedPieceForMovement, setSelectedPieceForMovement] = useState<
    string | null
  >(null);

  type PieceLevel = 'Low' | 'Intermediate' | 'High' | 'Checkmate';

  const classes: Array<PieceClass | "all"> = [
    "all",
    "ruling",
    "architectural",
    "religious",
    "military",
    "weaponry",
    "special",
  ];

  const levels: Array<PieceLevel | "all"> = ["all", "Low", "Intermediate", "High", "Checkmate"];

  const getPieceLevel = (abbreviation: string): PieceLevel => {
    const levelMap: Record<string, PieceLevel> = {
      // Low (L) - Forward moving, limited range
      'PWN': 'Low', 'KNV': 'Low', 'SQR': 'Low',
      // Intermediate (I) - Limited range in all directions
      'DUK': 'Intermediate', 'CHL': 'Intermediate', 'CDL': 'Intermediate', 'CTL': 'Intermediate',
      'ARM': 'Intermediate', 'ABY': 'Intermediate', 'ARB': 'Intermediate', 'CRD': 'Intermediate',
      'PRS': 'Intermediate', 'NUN': 'Intermediate', 'MSL': 'Intermediate', 'CPN': 'Intermediate',
      'SGT': 'Intermediate', 'GRD': 'Intermediate', 'ARC': 'Intermediate', 'KNT': 'Intermediate',
      'RAM': 'Intermediate', 'MAC': 'Intermediate', 'LNC': 'Intermediate', 'HCT': 'Intermediate',
      'SWD': 'Intermediate', 'SNT': 'Intermediate', 'WZD': 'Intermediate',
      // High (H) - Unlimited range in multiple directions
      'EPS': 'High', 'QUN': 'High', 'FTS': 'High', 'CSL': 'High', 'POP': 'High', 'BSP': 'High', 'CTP': 'High', 'AGL': 'High',
      // Checkmate (C) - Game-ending pieces
      'KNG': 'Checkmate', 'EPR': 'Checkmate',
    };
    return levelMap[abbreviation] || 'Intermediate';
  };

  // Filter pieces based on selected checkboxes (both class and level)
  const piecesToDisplay = Object.values(PIECE_DEFINITIONS).filter((piece) => {
    const classMatch = selectedClasses.includes("all") || selectedClasses.includes(piece.class);
    const levelMatch = selectedLevels.includes("all") || selectedLevels.includes(getPieceLevel(piece.abbreviation));
    return classMatch && levelMatch;
  });

  const handleClassToggle = (cls: PieceClass | "all") => {
    if (cls === "all") {
      // If "all" is selected, deselect everything else
      setSelectedClasses(["all"]);
    } else {
      setSelectedClasses((prev) => {
        // Remove "all" if it's selected
        const withoutAll = prev.filter((c) => c !== "all");
        
        // Toggle the clicked class
        if (withoutAll.includes(cls)) {
          const newSelection = withoutAll.filter((c) => c !== cls);
          // If nothing selected, default to "all"
          return newSelection.length === 0 ? ["all"] : newSelection;
        } else {
          return [...withoutAll, cls];
        }
      });
    }
  };

  const handleLevelToggle = (level: PieceLevel | "all") => {
    if (level === "all") {
      setSelectedLevels(["all"]);
    } else {
      setSelectedLevels((prev) => {
        const withoutAll = prev.filter((l) => l !== "all");
        if (withoutAll.includes(level)) {
          const newSelection = withoutAll.filter((l) => l !== level);
          return newSelection.length === 0 ? ["all"] : newSelection;
        } else {
          return [...withoutAll, level];
        }
      });
    }
  };

  const getClassColor = (pieceClass: PieceClass): string => {
    const colors: Record<PieceClass, string> = {
      ruling: "#4169E1",
      architectural: "#a38b5eff",
      religious: "#9370DB",
      military: "#DC143C",
      weaponry: "#bd96b7ff",
      special: "#FF8C00",
    };
    return colors[pieceClass];
  };

  const getPieceIcon = (abbreviation: string): string => {
    return `/pieces/${abbreviation.toLowerCase()}.png`;
  };

  const getLevelColor = (level: PieceLevel): string => {
    const colors: Record<PieceLevel, string> = {
      'Low': '#607D8B',
      'Intermediate': '#2196F3',
      'High': '#9C27B0',
      'Checkmate': '#F44336',
    };
    return colors[level];
  };

  const handlePieceClick = (piece: any) => {
    console.log("Piece clicked:", piece.abbreviation);
    setSelectedPieceForGraph(piece.abbreviation);
  };

  const handleViewMovement = (piece: any, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the card click
    console.log("View movement clicked for piece:", piece.abbreviation);
    setSelectedPieceForMovement(piece.abbreviation);
  };

  const selectedPieceDataForGraph = selectedPieceForGraph
    ? Object.values(PIECE_DEFINITIONS).find(
        (p) => p.abbreviation === selectedPieceForGraph
      )
    : null;

  const selectedPieceDataForMovement = selectedPieceForMovement
    ? Object.values(PIECE_DEFINITIONS).find(
        (p) => p.abbreviation === selectedPieceForMovement
      )
    : null;

  return (
    <div style={{ padding: "10px", fontFamily: "Arial, sans-serif" }}>
      <style>{`
        @media (max-width: 768px) {
          .piece-catalog-content {
            flex-direction: column !important;
          }
          .piece-catalog-sidebar {
            flex: 1 1 auto !important;
            width: 100% !important;
            margin-bottom: 20px;
            min-height: auto !important;
          }
        }
      `}</style>
      
      <PageHeader
        icon={TAB_DESCRIPTIONS.pieces.icon}
        title={TAB_DESCRIPTIONS.pieces.label}
        description={TAB_DESCRIPTIONS.pieces.description}
      />

      <div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
        <div style={{ display: "flex", gap: "20px", maxWidth: "1600px", width: "100%" }} className="piece-catalog-content">
          {/* Sidebar with Class Filters */}
          <div
            className="piece-catalog-sidebar"
            style={{
              flex: "0 0 220px",
              height: "fit-content",
              minHeight: "400px",
              backgroundColor: "white",
              border: "2px solid #333",
              borderRadius: "8px",
              padding: "20px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
          <h3
            style={{
              margin: "0 0 15px 0",
              fontSize: "18px",
              color: "#333",
              borderBottom: "2px solid #333",
              paddingBottom: "10px",
            }}
          >
            Filter by Class
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {classes.map((cls) => (
              <label
                key={cls}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                  padding: "6px 8px",
                  borderRadius: "5px",
                  backgroundColor: selectedClasses.includes(cls)
                    ? "#f0f0f0"
                    : "transparent",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!selectedClasses.includes(cls)) {
                    e.currentTarget.style.backgroundColor = "#f9f9f9";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selectedClasses.includes(cls)) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedClasses.includes(cls)}
                  onChange={() => handleClassToggle(cls)}
                  style={{
                    width: "16px",
                    height: "16px",
                    cursor: "pointer",
                  }}
                />
                {cls === "all" ? (
                  <span style={{ fontSize: "13px", fontWeight: selectedClasses.includes(cls) ? "bold" : "normal", color: "#333" }}>
                    All Classes
                  </span>
                ) : (
                  <span
                    style={{
                      backgroundColor: getClassColor(cls as PieceClass),
                      color: "white",
                      padding: "3px 10px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "capitalize",
                    }}
                  >
                    {cls}
                  </span>
                )}
              </label>
            ))}
          </div>

          {/* Filter by Level */}
          <h3
            style={{
              margin: "25px 0 15px 0",
              fontSize: "18px",
              color: "#333",
              borderBottom: "2px solid #333",
              paddingBottom: "10px",
            }}
          >
            Filter by Level
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {levels.map((level) => (
              <label
                key={level}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                  padding: "6px 8px",
                  borderRadius: "5px",
                  backgroundColor: selectedLevels.includes(level)
                    ? "#f0f0f0"
                    : "transparent",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!selectedLevels.includes(level)) {
                    e.currentTarget.style.backgroundColor = "#f9f9f9";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selectedLevels.includes(level)) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedLevels.includes(level)}
                  onChange={() => handleLevelToggle(level)}
                  style={{
                    width: "16px",
                    height: "16px",
                    cursor: "pointer",
                  }}
                />
                {level === "all" ? (
                  <span style={{ fontSize: "13px", fontWeight: selectedLevels.includes(level) ? "bold" : "normal", color: "#333" }}>
                    All Levels
                  </span>
                ) : (
                  <span
                    style={{
                      backgroundColor: getLevelColor(level as PieceLevel),
                      color: "white",
                      padding: "3px 10px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    {level}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Piece Grid */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
              gap: "12px",
              maxWidth: "1400px",
            }}
          >
          {piecesToDisplay.map((piece) => (
            <div
              key={piece.type}
              onClick={() => handlePieceClick(piece)}
              style={{
                border: "2px solid #333",
                borderRadius: "6px",
                padding: "10px",
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                transition: "transform 0.2s, box-shadow 0.2s",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
              }}
            >
              {/* Piece Image/Icon */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "94px",
                  backgroundColor: "#f0f0f0",
                  borderRadius: "5px",
                  marginBottom: "8px",
                  position: "relative",
                  overflow: "hidden",
                  border: "1px solid #ccc",
                }}
              >
                <img
                  src={getPieceIcon(piece.abbreviation)}
                  alt={piece.name}
                  style={{
                    maxWidth: "90%",
                    maxHeight: "90%",
                    objectFit: "contain",
                    transition: "transform 0.2s",
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    const parent = target.parentElement;
                    if (parent) {
                      const fallback = document.createElement("div");
                      fallback.style.fontSize = "60px";
                      fallback.style.color = getClassColor(piece.class);
                      fallback.textContent = "●";
                      parent.appendChild(fallback);
                    }
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "3px",
                    right: "3px",
                    backgroundColor: "rgba(0,0,0,0.7)",
                    color: "white",
                    padding: "1px 4px",
                    borderRadius: "3px",
                    fontSize: "10px",
                    fontWeight: "bold",
                  }}
                >
                  [{piece.index[0]},{piece.index[1]}]
                </div>
              </div>

              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "6px",
                  borderBottom: "1px solid #f0f0f0",
                  paddingBottom: "6px",
                }}
              >
                <div>
                  <h3 style={{ margin: "0 0 3px 0", fontSize: "14px" }}>
                    {piece.name}
                  </h3>
                  <div style={{ fontSize: "10px", color: "#666", display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    <span
                      style={{
                        backgroundColor: getClassColor(piece.class),
                        color: "white",
                        padding: "1px 5px",
                        borderRadius: "3px",
                        textTransform: "capitalize",
                        fontSize: "13px",
                        fontWeight: "bold",
                      }}
                    >
                      {piece.class}
                    </span>
                    <span
                      style={{
                        backgroundColor: getLevelColor(getPieceLevel(piece.abbreviation)),
                        color: "white",
                        padding: "1px 5px",
                        borderRadius: "3px",
                        fontSize: "13px",
                        fontWeight: "bold",
                      }}
                    >
                      {getPieceLevel(piece.abbreviation)}
                    </span>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: "17px",
                      fontWeight: "bold",
                      color: "#333",
                    }}
                  >
                    {piece.abbreviation}
                  </div>
                </div>
              </div>

              {/* Button Container */}
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                }}
              >
                {/* View Movement Button */}
                <button
                  onClick={(e) => handleViewMovement(piece, e)}
                  style={{
                    flex: 1,
                    padding: "5px",
                    backgroundColor: getClassColor(piece.class),
                    color: "white",
                    border: "none",
                    borderRadius: "3px",
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: "13px",
                                        transition: "opacity 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  ♞ Movement
                </button>

                {/* View Value Graph Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePieceClick(piece);
                  }}
                  style={{
                    flex: 1,
                    padding: "5px",
                    backgroundColor: getClassColor(piece.class),
                    color: "white",
                    border: "none",
                    borderRadius: "3px",
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: "13px",
                    transition: "opacity 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  📊 Values
                </button>
              </div>
            </div>
          ))}
        </div>
        </div>
        </div>
      </div>

      {/* Piece Value Graph Modal */}
      {selectedPieceForGraph && selectedPieceDataForGraph && (
        <PieceValueGraph
          pieceName={selectedPieceDataForGraph.name}
          pieceAbbreviation={selectedPieceDataForGraph.abbreviation}
          values={getPieceValues(selectedPieceForGraph)}
          pieceClass={selectedPieceDataForGraph.class}
          onClose={() => setSelectedPieceForGraph(null)}
        />
      )}

      {/* Piece Movement Diagram Modal */}
      {selectedPieceForMovement && selectedPieceDataForMovement && (
        <PieceMovementDiagram
          pieceName={selectedPieceDataForMovement.name}
          pieceAbbreviation={selectedPieceDataForMovement.abbreviation}
          pieceClass={selectedPieceDataForMovement.class}
          movements={selectedPieceDataForMovement.rules.movements}
          onClose={() => setSelectedPieceForMovement(null)}
        />
      )}
    </div>
  );
};

export default PieceCatalog;
